import { useTranslation } from 'react-i18next'

import type { MigrationMatrixRow } from './quickStudyPure'

function scenarioLabel(scenario: string): string {
  if (scenario === 'price_only') return 'Price only'
  if (scenario === 'package') return 'Package ladder'
  if (scenario === 'risk') return 'Risk reducers'
  if (scenario === 'competitor') return 'Competitor anchor'
  return scenario || 'Scenario'
}

function heatColor(value: number): string {
  if (value >= 60) return 'bg-[hsl(var(--chart-positive))]/25 text-foreground'
  if (value >= 35) return 'bg-[hsl(var(--chart-warning))]/25 text-foreground'
  return 'bg-[hsl(var(--chart-negative))]/15 text-foreground'
}

function money(value: number | null | undefined, currency: string): string {
  if (value == null || !Number.isFinite(value)) return 'Target ACV'
  const amount = value.toLocaleString('en-US', { maximumFractionDigits: 0 })
  // Prefix the study's currency symbol when known; fall back to the bare number rather
  // than a hardcoded "$" so EUR/GBP studies never show the wrong symbol.
  return currency ? `${currency}${amount}` : amount
}

export function MigrationMatrixChart({
  rows,
  currency,
}: {
  rows?: Array<MigrationMatrixRow> | null
  /** Currency symbol override; defaults to the symbol carried on the rows. */
  currency?: string
}) {
  const { t } = useTranslation('app')
  const data = Array.isArray(rows) ? rows : []
  const resolvedCurrency = (currency || data.find((r) => r.currency)?.currency || '').trim()
  if (data.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center rounded-md bg-twin-chat-fg/5 text-xs text-twin-chat-fg-muted" data-testid="qs-migration-empty">
        Run at least one pricing choice round to build the migration read.
      </div>
    )
  }

  return (
    <div className="w-full overflow-hidden rounded-xl border border-border/40 bg-background/50" data-testid="qs-migration-matrix">
      <div className="grid grid-cols-[1.2fr_1.4fr_0.8fr_0.8fr] gap-px bg-border/30 text-xs">
        <div className="bg-background/80 px-3 py-2 font-medium text-muted-foreground">Scenario</div>
        <div className="bg-background/80 px-3 py-2 font-medium text-muted-foreground">Lever at target</div>
        <div className="bg-background/80 px-3 py-2 text-right font-medium text-muted-foreground">{t('quickStudyChrome.targetShare')}</div>
        <div className="bg-background/80 px-3 py-2 text-right font-medium text-muted-foreground">None</div>
        {data.map((row, i) => (
          <div key={`${row.scenario}-${i}`} className="contents" data-testid={`qs-migration-row-${i}`}>
            <div className="bg-background px-3 py-2 text-sm text-foreground">{scenarioLabel(row.scenario)}</div>
            <div className="bg-background px-3 py-2">
              <div className="text-sm text-foreground">{row.lever_level || money(row.anchor_price, resolvedCurrency)}</div>
              {row.price_ladder && row.price_ladder.length > 0 && (
                <div className="mt-1 text-[11px] text-muted-foreground">
                  Ladder: {row.price_ladder.map((p) => money(p, resolvedCurrency)).join(' · ')}
                </div>
              )}
            </div>
            <div className={`px-3 py-2 text-right text-sm font-semibold tabular-nums ${heatColor(row.share_at_anchor_pct)}`} data-testid={`qs-migration-share-${i}`}>
              {row.share_at_anchor_pct}%
            </div>
            <div className="bg-background px-3 py-2 text-right text-sm tabular-nums text-muted-foreground" data-testid={`qs-migration-none-${i}`}>
              {row.none_pct}%
            </div>
          </div>
        ))}
      </div>
      <p className="px-3 py-2 text-[11px] text-muted-foreground">
        Fixed-order cross-sectional read: compare the target share and rejection share by scenario; do not read this as individual buyer movement.
      </p>
    </div>
  )
}
